import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Paper,
    ThemeProvider,
    CircularProgress,
} from "@mui/material";
import { GruppenApi } from "../../services/api";
import Notification from "../util/notifications/Notification";
import NavBar from "../layout/NavBar";
import { darkTheme } from "../../theme/darkTheme";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../../services/socket"; // ⭐ NEU
import KommentarBubble from "./components/kommentare/KommentarBubble";
import KommentarInput from "./components/kommentare/KommentarInput";

const Kommentare = () => {
    const [, setShowNotification] = useState(false);
    const { nutzer } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [kommentare, setKommentare] = useState([]);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [newKommentar, setNewKommentar] = useState("");
    const messagesEndRef = useRef(null);
    const socketRef = useRef(null); // ⭐ NEU: Socket Referenz

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (kommentare.length > 0) {
            scrollToBottom();
        }
    }, [kommentare]);

    const fetchKommentare = async () => {
        setLoading(true);
        try {
            const response = await GruppenApi.getKommentare(id);
            setKommentare(response.data);
        } catch (err) {
            console.error("Error fetching comments:", err);
            setMessage({ type: "error", text: "Fehler beim Laden der Kommentare." });
        } finally {
            setLoading(false);
        }
    };

    // ⭐ NEU: Socket.io Setup und Cleanup
    useEffect(() => {
        if (!nutzer || !id) return;

        // Socket initialisieren
        socketRef.current = getSocket();
        const socket = socketRef.current;

        // Termin-Room betreten
        socket.emit('join-termin', id);
        console.log(`[Socket.io Client] Joined termin-${id}`);

        // Listener für neue Kommentare
        const handleNeuerKommentar = (kommentar) => {
            console.log('[Socket.io Client] 📨 Neuer Kommentar empfangen:', kommentar);

            // Nur hinzufügen, wenn nicht vom aktuellen Nutzer
            if (kommentar.user_id !== nutzer.id) {
                setKommentare(prev => [...prev, kommentar]);
            }
        };

        socket.on('neuer-kommentar', handleNeuerKommentar);

        // Initiales Laden
        fetchKommentare();

        // Cleanup beim Verlassen
        return () => {
            socket.off('neuer-kommentar', handleNeuerKommentar);
            socket.emit('leave-termin', id);
            console.log(`[Socket.io Client] Left termin-${id}`);
        };
    }, [id, nutzer]);

    const handleChange = (e) => {
        setNewKommentar(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newKommentar.trim()) return;

        try {
            const userId = nutzer.id;
            const response = await GruppenApi.addKommentar(id, {
                user_id: userId,
                kommentar: newKommentar,
            });

            if (response.status === 201) {
                // ⭐ NEU: Optimistisches Update - Kommentar sofort anzeigen
                const neuesKommentar = response.data.kommentar || {
                    id: response.data.id,
                    termin_id: id,
                    user_id: userId,
                    text: newKommentar,
                    erstellt_am: new Date().toISOString(),
                    nutzer_info: {
                        vname: nutzer.vname,
                        nname: nutzer.nname
                    }
                };

                setKommentare(prev => [...prev, neuesKommentar]);
                setNewKommentar("");

                // Server sendet automatisch via Socket.io an andere Clients
            }
        } catch (err) {
            console.error("Error submitting comment:", err);
            setMessage({ type: "error", text: "Fehler beim Senden des Kommentars." });
        }
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <Box
                sx={{
                    bgcolor: "background.default",
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <NavBar />

                {message.status === "error" || message.status === "success" && (
                    <Notification
                        type={message.type}
                        message={message.text}
                        onClose={() => {
                            setShowNotification(true);
                            setMessage("");
                        }}
                    />
                )}

                <Container
                    maxWidth="sm"
                    sx={{
                        pt: 2,
                        px: { xs: 1, sm: 2 },
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        pb: { xs: "54px", md: 2 },
                    }}
                >
                    {/* Messages Container */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            mb: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        {loading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    py: 4,
                                }}
                            >
                                <CircularProgress sx={{ color: "#3b82f6" }} />
                            </Box>
                        ) : kommentare.length === 0 ? (
                            <Paper
                                sx={{
                                    p: 3,
                                    bgcolor: "#1e293b",
                                    textAlign: "center",
                                    borderRadius: "16px",
                                    border: "1px solid rgba(59, 130, 246, 0.3)",
                                }}
                            >
                                <Typography color="#93c5fd">
                                    Noch keine Kommentare vorhanden
                                </Typography>
                            </Paper>
                        ) : (
                            kommentare.map((k, i) => {
                                const kommentarUserId = k.nutzer_id || k.user_id;
                                const isOwnMessage = nutzer && kommentarUserId === nutzer.id;

                                return (
                                    <KommentarBubble
                                        key={k.id || i}
                                        kommentar={k}
                                        isOwnMessage={isOwnMessage}
                                        nutzer={nutzer}
                                    />
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </Box>
                </Container>

                {/* Input Area - Fixed above NavBarBot */}
                <KommentarInput
                    value={newKommentar}
                    onChange={handleChange}
                    onSubmit={handleSubmit}
                    onBack={() => navigate(-1)}
                />
            </Box>
        </ThemeProvider>
    );
};

export default Kommentare;
